import {
  Component,
  effect,
  inject,
  model,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { IRecetaData } from '@core/interfaces/entidades/receta.entidad';
import { ICategoriaData } from '@core/interfaces/entidades/categoria.entidad';
import { RecetaApiService } from '@core/services/api/receta-api.service';
import { CategoriaApiService } from '@core/services/api/categoria-api.service';
import { AppService } from '@shared/services/app.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { ToastService } from '@shared/services/toast.service';
import { Tabla } from '@shared/components/tabla/tabla';
import { ModalConfirmacion } from '@shared/components/modal-confirmacion/modal-confirmacion';
import { CrearProducto } from '@shared/components/crear-producto/crear-producto';
import { UiButton, UiDialog, UiTooltip } from '@shared/components/ui';
import { RecetaForm } from './components/receta-form/receta-form.component';
import { ETipoProducto } from '@core/enums/tipoProducto.enum';

@Component({
  selector: 'app-receta',
  imports: [
    CommonModule,
    Tabla,
    ModalConfirmacion,
    CrearProducto,
    UiButton,
    UiDialog,
    RecetaForm,
    CurrencyPipe,
    UiTooltip,
  ],
  templateUrl: './receta.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './receta.css',
})
export class Receta implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarRecetas();
        this.categoriaApiService.cargar().subscribe({
          next: (r) => this.categorias.set(r),
          error: () => {},
        });
      }
    });
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Recetas',
      subtitle: 'Administra las recetas, ingredientes y costos de tus platos.',
      showBackButton: true,
      icon: 'bi bi-journal-text',
    });
    this.columnasTabla = [
      { key: 'producto_receta_codigo', titulo: 'Código' },
      { key: 'nombre', titulo: 'Receta' },
      { key: 'costo_estimado', titulo: 'Costo prod.', template: this.costoTemplate() },
      {
        key: 'producto_receta_precio_venta',
        titulo: 'Precio venta',
        template: this.precioVentaTemplate(),
      },
      { key: 'margen', titulo: 'Margen', template: this.margenTemplate() },
      { key: 'detalles', titulo: 'Ingredientes', template: this.ingredientesTemplate() },
      { key: 'activo', titulo: 'Estado', template: this.activoTemplate() },
    ];
  }

  costoTemplate = viewChild('costoTemplate');
  precioVentaTemplate = viewChild('precioVentaTemplate');
  margenTemplate = viewChild('margenTemplate');
  ingredientesTemplate = viewChild('ingredientesTemplate');
  activoTemplate = viewChild('activoTemplate');

  private recetaApiService = inject(RecetaApiService);
  private categoriaApiService = inject(CategoriaApiService);
  private toastService = inject(ToastService);
  private appService = inject(AppService);
  private sucursalService = inject(SucursalService);

  recetas = signal<IRecetaData[]>([]);
  categorias = signal<ICategoriaData[]>([]);
  recetaSeleccionada = signal<IRecetaData | undefined>(undefined);
  recetaAEliminar = signal<IRecetaData | undefined>(undefined);
  readonly etipoReceta = ETipoProducto.RECETA;

  modal_crear_producto = model<boolean>(false);
  modal_editar = model<boolean>(false);
  modal_duplicar = model<boolean>(false);
  modal_eliminar = model<boolean>(false);

  columnasTabla!: ITablaColumna[];

  cargarRecetas() {
    this.recetaApiService.obtenerRecetas().subscribe({
      next: (response) => this.recetas.set(response),
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message ?? 'No se pudo cargar recetas.',
        );
      },
    });
  }

  onProductoCreado() {
    this.modal_crear_producto.set(false);
    this.cargarRecetas();
  }

  onEditar(receta: IRecetaData) {
    this.recetaSeleccionada.set(receta);
    this.modal_editar.set(true);
  }

  onCerrarEditar(receta?: IRecetaData) {
    this.modal_editar.set(false);
    this.recetaSeleccionada.set(undefined);
    if (receta) this.cargarRecetas();
  }

  onDuplicar(receta: IRecetaData) {
    this.recetaSeleccionada.set(receta);
    this.modal_duplicar.set(true);
  }

  onCerrarDuplicar(receta?: IRecetaData) {
    this.modal_duplicar.set(false);
    this.recetaSeleccionada.set(undefined);
    if (receta) this.cargarRecetas();
  }

  solicitarEliminar(receta: IRecetaData) {
    this.recetaAEliminar.set(receta);
    this.modal_eliminar.set(true);
  }

  eliminar() {
    const receta = this.recetaAEliminar();
    if (!receta) return;
    this.recetaApiService.eliminar(receta.producto_receta_id).subscribe({
      next: () => {
        this.toastService.show('top-right', 'success', 'Éxito', 'Receta eliminada.');
        this.recetas.update((list) =>
          list.filter((r) => r.producto_receta_id !== receta.producto_receta_id),
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message ?? 'No se pudo eliminar.',
        );
      },
      complete: () => {
        this.modal_eliminar.set(false);
        this.recetaAEliminar.set(undefined);
      },
    });
  }

  cerrarModalEliminar() {
    this.modal_eliminar.set(false);
    this.recetaAEliminar.set(undefined);
  }
}
